import { LoadingScreen } from '@/src/design-system/loading/Screen'
import { Redirect } from 'expo-router'
import { useEffect, useRef, type FunctionComponent } from 'react'
import { useNewestApplication } from '@/src/experiences/card-application/hooks/useNewestApplication'

type FinishedStatus = Extract<
  NonNullable<
    NonNullable<ReturnType<typeof useNewestApplication>['data']>['newestApplication']
  >['status'],
  'ISSUED' | 'APPROVAL_ACKED' | 'DENIED'
>

/**
 * some `/card-application` screens, namely `ApplicationApproved` and `ApplicationDenied`, should not be rendered if the application is not finished.
 * if some naughty user tries to deeplink here, we should redirect them to the `/card` root (which itself would navigate to the `/card/cardBenefits` or `/card/card-management` screen accordingly).
 *
 * the difference between "finished" and "unfinished" is not arbitrary - "finished" applications' statuses are stable and can thus be handled in a reliable way without worry of redundant redirects or race conditions.
 * "unfinished" applications' statuses on the other hand will change over time and should handle their own gates individually (see [notes/learnings on _layout vs per-route gates/redirects](https://docs.google.com/document/d/1tfMdtrZdWM6Cx5sseU3gc676VvJ0dWG9U_UC6DKsKZQ/edit?tab=t.0#bookmark=id.1n6telhkopnv))
 */
export function withFinishedApplicationGate<TProps extends Record<string, unknown>>(
  ToWrap: FunctionComponent<TProps>,
  overrideRender?: (
    finishedStatus: FinishedStatus,
  ) => null | undefined | ReturnType<FunctionComponent>,
) {
  return function Wrapped(props: TProps) {
    const query = useNewestApplication()

    // Refetch the query when the component mounts. This prevents a bug where the user gets
    // redirected to the card benefits screen due to stale data.
    const mountedRef = useRef<boolean>(false)
    useEffect(() => {
      if (!mountedRef.current) {
        mountedRef.current = true
        void query.refetch()
      }
    }, [query])

    if (query.isUninitialized || query.isLoading) {
      return <LoadingScreen />
    }

    if (
      !query.data ||
      query.data.eligibility.canStartCardApplication ||
      !query.data.newestApplication
    ) {
      return <Redirect href="/main/card" />
    }

    switch (query.data.newestApplication.status) {
      case 'ISSUED':
      case 'APPROVAL_ACKED':
      case 'DENIED': {
        true satisfies typeof query.data.newestApplication.status extends FinishedStatus
          ? FinishedStatus extends typeof query.data.newestApplication.status
            ? true
            : false
          : false
        const overridingRender = overrideRender?.(query.data.newestApplication.status)
        if (overridingRender) return overridingRender
        break
      }
      default:
        return query.isFetching ? <LoadingScreen /> : <Redirect href="/main/card" />
    }

    return <ToWrap {...props} />
  }
}
